import { cn, delay } from "kz-ui-sdk";
import { useCallback, useEffect, useRef, useState } from "react";

export interface RollingNumberRef {
  rollAll: () => void;
  stopAll?: () => void;
}

interface RollingNumberProps {
  claimedPoint: string;
  hideShadow?: boolean;
}

const DIGITS = ["9", "8", "7", "6", "5", "4", "3", "2", "1", "0", ".", ","];

const RollingNumber = ({ claimedPoint, hideShadow = false }: RollingNumberProps) => {
  const [isShifted, setIsShifted] = useState(false);
  const animations = useRef<any>([]);

  const startSlotAnimation = useCallback(
    (ref: HTMLDivElement | null, index: number) => {
      const factor = 5 + Math.floor(Math.random() * 10);
      const section = 100 / DIGITS.length;
      let spinTimer: number;

      // Initialize with a random number
      const randomStart = Math.floor(Math.random() * DIGITS.length);
      if (ref) {
        ref.style.transform = `translateY(-${randomStart * section}%)`;
        ref.style.opacity = "1";
      }

      // Spin function using requestAnimationFrame for smoother animation
      const spin = () => {
        if (ref) ref.style.transition = "";
        let value = randomStart * section; // Start from the random position

        const animate = () => {
          if (value <= -(section * DIGITS.length)) {
            value = 0;
          }
          value -= section / factor;
          if (ref) {
            ref.style.transform = `translateY(${-value}%)`;
            ref.style.opacity = "1";
          }
          spinTimer = requestAnimationFrame(animate);
        };
        spinTimer = requestAnimationFrame(animate);
      };

      const stop = (delay: number | undefined) => {
        setTimeout(() => {
          cancelAnimationFrame(spinTimer);
          if (ref) {
            ref.style.transition = "transform 1.5s ease-out";
            const claimedPointString = claimedPoint;
            const targetNumber = claimedPointString[index];
            const targetIndex = DIGITS.length - DIGITS.indexOf(targetNumber);
            const targetPosition = section * targetIndex - section;

            // reset the position of the wheel
            ref.style.opacity = "0";
            ref.style.transform = `translateY(-128px)`;
            ref.style.transitionDuration = "0.1s";
            ref.style.transitionProperty = "transform";

            // scroll down to the target position
            setTimeout(() => {
              ref.style.transitionDuration = "1.5s";
              ref.style.transform = `translateY(${targetPosition}%)`;
              ref.style.opacity = "1";
              if (targetPosition === -81.81818181818183 && claimedPointString[index] === "-") {
                ref.style.transition = "transform 1.5s ease-out";
                ref.style.opacity = "0";
              }
            }, 100);
          }
        }, delay);
      };

      return { spin, stop };
    },
    [claimedPoint],
  );

  useEffect(() => {
    if (claimedPoint) {
      const amountStr = claimedPoint;
      const amountStrLength = amountStr.length;
      animations.current = Array.from({
        length: amountStrLength,
      }).map((_, index) => {
        const reverseIndex = amountStrLength - index - 1;
        const wheelRef = document.getElementById(["daily-reward-wheel", reverseIndex].join("-")) as HTMLDivElement;
        const animation = startSlotAnimation(wheelRef, reverseIndex);
        animation.spin();
        delay(2500).then(() => animation.stop(index * 300));
        delay(6500).then(() => setIsShifted(true));
        return animation;
      });
    }
  }, [claimedPoint, startSlotAnimation]);

  return (
    <>
      <div className="absolute bottom-[-10px] flex h-[120px] w-full items-baseline justify-center overflow-hidden">
        {!hideShadow && (
          <div
            className={cn(
              "absolute left-1/2 top-[60px] z-20 h-[60px] w-[263px] -translate-x-1/2 bg-[linear-gradient(0deg,#00000091,transparent)] transition-opacity",
              {
                "opacity-0": isShifted,
              },
            )}
          ></div>
        )}

        <div className={cn("-translate-y-[calc(100%-128px)]")}>
          {Array.from({
            length: claimedPoint.length,
          }).map((_, wheelIndex) => {
            const currentChar = claimedPoint[wheelIndex];
            const isPunctuation = [".", ","].includes(currentChar);
            const isEmpty = currentChar === "-" || currentChar === undefined;

            return (
              <div
                className={cn(
                  "inline-block max-w-[29px] animate-fade-in-3s overflow-hidden transition-all duration-500 will-change-transform",
                  {
                    "mx-[-8px]": isShifted && isPunctuation,
                    "drop-shadow-[0px_0px_5px_rgba(245,193,42,0.38)]": isShifted,
                    "!max-w-0": isShifted && isEmpty,
                  },
                )}
                key={["wheel", wheelIndex].join("-")}
              >
                <section>
                  <div
                    className={cn("absolute w-[29px] text-center")}
                    style={{
                      top: 0,
                      position: "relative",
                    }}
                    id={["daily-reward-wheel", wheelIndex].join("-")}
                  >
                    {DIGITS.map((num, index) => (
                      <div
                        className="animate-fade-in pb-6 will-change-transform"
                        key={["wheel", wheelIndex, num].join("-")}
                      >
                        <p
                          className={cn(
                            "!font-noto-thai text-center text-[46px] font-bold tracking-[-2.3px] text-[#ffd966] [text-shadow:0_4px_10px_rgba(0,0,0,0.25)]",
                            {
                              "text-gradient-gold-2": isShifted,
                            },
                          )}
                        >
                          {num}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default RollingNumber;
